import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import ChangePasswordSection from '../components/settings/ChangePasswordSection';
import RecentSessionsSection from '../components/settings/RecentSessionsSection';

const API = process.env.REACT_APP_BACKEND_URL;

const Settings = ({ userProfile, setUserProfile }) => {
  const { switchTheme, currentTheme } = useTheme();
  const navigate = useNavigate();
  // Ouvre l'onglet Sécurité si on arrive depuis le lien du toast de session
  // (flag sessionStorage fiable malgré le routeur hybride legacy) ou via /settings#securite.
  const [activeTab, setActiveTab] = useState(() =>
    sessionStorage.getItem('komerces_open_security') === '1' || window.location.hash === '#securite'
      ? 'security'
      : 'user'
  );
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  // User settings (only app preferences, not personal info)
  const [userSettings, setUserSettings] = useState({
    langue: 'fr',
    theme: 'moderne',
    notifications_enabled: true
  });

  // Agency settings
  const [agencySettings, setAgencySettings] = useState({
    nom_agence: '',
    adresse_complete: '',
    code_postal: '',
    ville: '',
    telephone_agence: '',
    email_agence: '',
    logo_url: '',
    site_web: '',
    facebook: '',
    instagram: '',
    linkedin: '',
    horaires_ouverture: '',
    siret: ''
  });

  // Load settings on mount
  useEffect(() => {
    loadUserSettings();
    loadAgencySettings();
    sessionStorage.removeItem('komerces_open_security');
  }, []);

  const loadUserSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/user`);
      setUserSettings(response.data);
      // Synchroniser le thème avec le contexte
      if (response.data.theme) {
        switchTheme(response.data.theme);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres utilisateur:', error);
    }
  };

  const loadAgencySettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/agency`);
      setAgencySettings(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres agence:', error);
    }
  };

  const handleUserChange = (field, value) => {
    setUserSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleAgencyChange = (field, value) => {
    setAgencySettings(prev => ({ ...prev, [field]: value }));
  };

  const saveUserSettings = async () => {
    setLoading(true);
    setSaveMessage('');
    try {
      // Get current user data to preserve personal info
      const currentData = await axios.get(`${API}/api/settings/user`);
      
      // Merge preferences with existing personal data
      const updatedSettings = {
        ...currentData.data,
        langue: userSettings.langue,
        theme: userSettings.theme,
        notifications_enabled: userSettings.notifications_enabled
      };
      
      await axios.put(`${API}/api/settings/user`, updatedSettings);
      
      // Update theme
      if (userSettings.theme) {
        switchTheme(userSettings.theme);
      }
      
      setSaveMessage('✅ Paramètres sauvegardés!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setSaveMessage('❌ Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const saveAgencySettings = async () => {
    setLoading(true);
    setSaveMessage('');
    try {
      await axios.put(`${API}/api/settings/agency`, agencySettings);
      setSaveMessage('✅ Paramètres de l\'agence sauvegardés!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setSaveMessage('❌ Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Paramètres</h1>
          <p className="text-gray-600 mt-2">Gérez vos informations personnelles et celles de votre agence</p>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={`mb-4 p-4 rounded-lg ${
            saveMessage.includes('✅') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {saveMessage}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('user')}
              className={`flex-1 px-3 md:px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'user'
                  ? 'border-b-2 border-[#007BFF] text-[#007BFF]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="bi bi-gear mr-2"></i>
              Préférences
            </button>
            <button
              onClick={() => setActiveTab('agency')}
              className={`flex-1 px-3 md:px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'agency'
                  ? 'border-b-2 border-[#007BFF] text-[#007BFF]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="bi bi-building mr-2"></i>
              Informations Agence
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`flex-1 px-3 md:px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'security'
                  ? 'border-b-2 border-[#007BFF] text-[#007BFF]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="settings-tab-security"
            >
              <i className="bi bi-shield-lock mr-2"></i>
              Sécurité
            </button>
          </div>
        </div>

        {/* Security Tab */}
        {activeTab === 'security' && (
          <>
            <ChangePasswordSection />
            <RecentSessionsSection />
          </>
        )}

        {/* User Preferences Tab */}
        {activeTab === 'user' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-6 text-gray-900">Préférences de l&apos;Application</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Langue
                </label>
                <select
                  value={userSettings.langue || 'fr'}
                  onChange={(e) => handleUserChange('langue', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                >
                  {/* lot DL — l'interface n'existe qu'en français */}
                  <option value="fr">Français</option>
                </select>
              </div>

              <div className="flex items-center md:col-span-2">
                <input
                  type="checkbox"
                  checked={userSettings.notifications_enabled}
                  onChange={(e) => handleUserChange('notifications_enabled', e.target.checked)}
                  className="w-4 h-4 text-[#007BFF] border-gray-300 rounded focus:ring-[#007BFF]"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Activer les notifications
                </label>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={saveUserSettings}
                disabled={loading}
                className="px-6 py-2.5 bg-[#28A745] text-white rounded-lg font-medium shadow-sm hover:bg-[#218838] transition-colors disabled:bg-gray-400"
              >
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}

        {/* Agency Settings Tab */}
        {activeTab === 'agency' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-6 text-gray-900">Informations de l&apos;Agence</h2>
            
            <div className="space-y-6">
              {/* Section: Informations Générales */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Informations Générales</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom de l&apos;agence *
                    </label>
                    <input
                      type="text"
                      value={agencySettings.nom_agence || ''}
                      onChange={(e) => handleAgencyChange('nom_agence', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="KOMERCES"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse complète
                    </label>
                    <input
                      type="text"
                      value={agencySettings.adresse_complete || ''}
                      onChange={(e) => handleAgencyChange('adresse_complete', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="123 Rue Example"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Code postal
                    </label>
                    <input
                      type="text"
                      value={agencySettings.code_postal || ''}
                      onChange={(e) => handleAgencyChange('code_postal', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="75001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ville
                    </label>
                    <input
                      type="text"
                      value={agencySettings.ville || ''}
                      onChange={(e) => handleAgencyChange('ville', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="Paris"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SIRET
                    </label>
                    <input
                      type="text"
                      value={agencySettings.siret || ''}
                      onChange={(e) => handleAgencyChange('siret', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="XXX XXX XXX XXXXX"
                    />
                  </div>
                </div>
              </div>

              {/* Section: Contact */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={agencySettings.telephone_agence || ''}
                      onChange={(e) => handleAgencyChange('telephone_agence', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="+33 X XX XX XX XX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={agencySettings.email_agence || ''}
                      onChange={(e) => handleAgencyChange('email_agence', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="contact@agence.fr"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Horaires d&apos;ouverture
                    </label>
                    <textarea
                      value={agencySettings.horaires_ouverture || ''}
                      onChange={(e) => handleAgencyChange('horaires_ouverture', e.target.value)}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="Lun-Ven: 9h-12h / 14h-18h&#10;Sam: 9h-12h&#10;Dim: Fermé"
                    />
                  </div>
                </div>
              </div>

              {/* Section: Web & Réseaux Sociaux */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Web & Réseaux Sociaux</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site web
                    </label>
                    <input
                      type="url"
                      value={agencySettings.site_web || ''}
                      onChange={(e) => handleAgencyChange('site_web', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="https://www.votre-agence.fr"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="bi bi-facebook mr-2"></i>Facebook
                    </label>
                    <input
                      type="url"
                      value={agencySettings.facebook || ''}
                      onChange={(e) => handleAgencyChange('facebook', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="https://facebook.com/votre-page"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="bi bi-instagram mr-2"></i>Instagram
                    </label>
                    <input
                      type="url"
                      value={agencySettings.instagram || ''}
                      onChange={(e) => handleAgencyChange('instagram', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="https://instagram.com/votre-compte"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="bi bi-linkedin mr-2"></i>LinkedIn
                    </label>
                    <input
                      type="url"
                      value={agencySettings.linkedin || ''}
                      onChange={(e) => handleAgencyChange('linkedin', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
                      placeholder="https://linkedin.com/company/votre-agence"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={saveAgencySettings}
                disabled={loading}
                className="px-6 py-2.5 bg-[#28A745] text-white rounded-lg font-medium shadow-sm hover:bg-[#218838] transition-colors disabled:bg-gray-400"
              >
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
